import { Row } from "@tanstack/react-table";
import {
  CalendarPlus,
  CalendarX,
  Edit,
  Eye,
  GitCompareArrows,
  History,
  Lock,
  MoreHorizontal,
  Package
} from "lucide-react";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
// import { useDialogModal } from "@/hook/handle-modal/hooks/actions";
import { isDevelopment, isHomolog } from "@/utils/environment";

import { CampaignType, CampaignVisualStatus, IUnifiedCampaign } from "@/@types/campaign";
import { endsAfterToday } from "@/pages/business-planning/campaigns/utils/end-campaign";
import { END_CAMPAIGN_LABELS } from "@/pages/business-planning/campaigns/utils/messages";

import { UNIFIED_CAMPAIGN_ROW_TEXTS } from "./constants";

interface UnifiedCampaignRowActionsProps {
  row: Row<IUnifiedCampaign>;
  onViewDetails: (campaign: IUnifiedCampaign) => void;
  onEdit?: (campaign: IUnifiedCampaign) => void;
  onViewProducts?: (campaign: IUnifiedCampaign) => void;
  onExtend?: (campaign: IUnifiedCampaign) => void;
  onViewHistory?: (campaign: IUnifiedCampaign) => void;
  onEnd?: (campaign: IUnifiedCampaign) => void;
}

export function UnifiedCampaignRowActions({
  row,
  onViewDetails,
  onEdit,
  onViewProducts,
  onExtend,
  onViewHistory,
  onEnd,
}: UnifiedCampaignRowActionsProps) {
  const campaign = row.original;
  const navigate = useNavigate();
  // const { handleModal } = useDialogModal();

  const isPro012 = campaign.type === CampaignType.PRO012;
  const isPro014 = campaign.type === CampaignType.PRO014;
  const isPro017 = campaign.type === CampaignType.PRO017;
  // Disparo manual de vigência: só PRO014/PRO017 (têm SKU) e só fora de produção (QA).
  const canTriggerValidity =
    (isPro014 || isPro017) && (isHomolog() || isDevelopment());
  const isExpired = campaign.visualStatus === "EXPIRED";
  const canEdit = campaign.canEdit && !isPro012 && !isExpired;
  const canExtend = !isPro012 && campaign.visualStatus === CampaignVisualStatus.ACTIVE;
  const canEnd: boolean =
    !isPro012 && !isExpired && endsAfterToday(campaign.endDate);
  // Histórico só pra tipos auditados (PRO013/14/17). PRO012 é legado.
  const hasHistory =
    campaign.type === CampaignType.PRO013 ||
    campaign.type === CampaignType.PRO014 ||
    campaign.type === CampaignType.PRO017;
  // Comparativo retaguarda vs loja: mesmos tipos auditados (PRO012 não propaga).
  const canCompare = hasHistory;

  // Separator antes do bloco de ações (Editar/Prorrogar/Histórico) só uma vez
  // — se algum desses estiver disponível.
  const hasActionsBlock =
    (canEdit && !!onEdit) ||
    (canExtend && !!onExtend) ||
    (canEnd && !!onEnd) ||
    (hasHistory && !!onViewHistory);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className="flex h-8 w-8 p-0 data-[state=open]:bg-muted"
        >
          <MoreHorizontal className="h-4 w-4" />
          <span className="sr-only">Abrir menu</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[200px]">
        {isPro012 && (
          <>
            <DropdownMenuLabel className="flex items-start text-xs text-muted-foreground">
              <Lock className="mr-1 mt-0.5 h-3 w-3 shrink-0" />
              <span className="whitespace-normal">
                {UNIFIED_CAMPAIGN_ROW_TEXTS.READ_ONLY}
              </span>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
          </>
        )}

        <DropdownMenuItem onClick={() => onViewDetails(campaign)}>
          <Eye className="mr-2 h-4 w-4" />
          Ver Detalhes
        </DropdownMenuItem>

        {isPro014 && onViewProducts && (
          <DropdownMenuItem onClick={() => onViewProducts(campaign)}>
            <Package className="mr-2 h-4 w-4" />
            Visualizar Produtos
          </DropdownMenuItem>
        )}

        {canCompare && (
          <DropdownMenuItem
            onClick={() =>
              navigate(
                `/planejamento-comercial/comparativo-retaguarda/${campaign.type}/${campaign.code}`
              )
            }
          >
            <GitCompareArrows className="mr-2 h-4 w-4" />
            Ver comparativo
          </DropdownMenuItem>
        )}

        {hasActionsBlock && <DropdownMenuSeparator />}

        {canEdit && onEdit && (
          <DropdownMenuItem onClick={() => onEdit(campaign)}>
            <Edit className="mr-2 h-4 w-4" />
            Editar
          </DropdownMenuItem>
        )}

        {canExtend && onExtend && (
          <DropdownMenuItem onClick={() => onExtend(campaign)}>
            <CalendarPlus className="mr-2 h-4 w-4" />
            Prorrogar
          </DropdownMenuItem>
        )}

        {canEnd && onEnd && (
          <DropdownMenuItem onClick={() => onEnd(campaign)}>
            <CalendarX className="mr-2 h-4 w-4" />
            {END_CAMPAIGN_LABELS.ACTION}
          </DropdownMenuItem>
        )}

        {hasHistory && onViewHistory && (
          <DropdownMenuItem onClick={() => onViewHistory(campaign)}>
            <History className="mr-2 h-4 w-4" />
            Histórico de alterações
          </DropdownMenuItem>
        )}

        {canTriggerValidity && (
          <>
            <DropdownMenuSeparator />
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
